import { Items, RecordCreate, RecordMerge } from "@pdfking/tfunc"
import type { ComponentChildren, FunctionComponent, JSX } from "preact"
import { Router as IsoRouter, LocationProvider, Route } from "preact-iso"
import { exec } from "preact-iso/router"
import { useEffect, useMemo, useState } from "preact/hooks"

import type { RouteCodec, RouteConfig } from "#internal"
import { DataCodec, FillPath } from "#internal"

type PropsOf<R> = R extends { view: FunctionComponent<infer P> } ? P : never

// Every route checked against its own view's props and its own path's params:
// `defaults` and the codec's `decode` must produce the view's props, `encode`
// every required param of the path. A codec's lambdas annotate their
// parameters — inside a record TS can't infer them per route, but checks them.
type Checked<R> = {
  [K in keyof R]: R[K] extends { path: infer P extends string }
    ? RouteConfig<PropsOf<R[K]>, P>
    : never
}

type Params = Record<string, string>
type State = Record<string, unknown>

// Hash routes match under this prefix, so one preact-iso Router can hold both
// kinds: "/about" is a path route, "/#/viewer/…" a hash one. Never a real
// path — `ReadPath` builds it, nothing parses it as a url.
const HASH = "/#"

// What preact-iso's Router matches: the hash when it holds a route, the path
// otherwise — a hash route always wins, so a hash-routed view can open on top
// of any page.
const ReadPath = (): string =>
  location.hash.startsWith("#/") ? `${HASH}${location.hash.slice(1)}` : location.pathname

// pushState/replaceState emit nothing, so every router re-reads on popstate.
const Write = (href: string, replace: boolean): void => {
  history[replace ? "replaceState" : "pushState"](null, "", href)
  dispatchEvent(new PopStateEvent("popstate"))
}

export const CreateRouter = <const R extends Checked<R>>(config: R) => {
  const routes = Items(config as unknown as Record<string, RouteConfig<State, string>>).map(
    ([name, route]) => {
      const defaults = route.defaults
      const codec = (route.codec ?? DataCodec) as RouteCodec<State, string>
      const hash = route.hash ?? true
      const View = route.view
      return {
        name,
        path: route.path,
        defaults,
        codec,
        hash,
        pattern: hash ? `${HASH}${route.path}` : route.path,
        // matched by url: the path params, through the codec
        Matched: ({ params }: { params: Params }) => <View {...codec.decode(params, defaults)} />,
        // the Router's `name`, when nothing in the url matches
        Fallback: ({ data }: { data?: Record<string, unknown> }) => (
          <View {...defaults} {...data} />
        ),
      }
    }
  )
  const byName = RecordCreate(routes.map(route => [route.name, route] as const))

  // The route's url — "#/viewer/…" or "/users/42" — exactly as Navigate
  // writes it, defaults elided. For handing it somewhere other than this
  // document's location: a new tab, a second window, an email, an <a>.
  const CreateHref = <K extends keyof R & string>(
    name: K,
    data: Partial<PropsOf<R[K]>> = {}
  ): string => {
    const route = byName[name]!
    const props = RecordMerge(route.defaults, data)
    const path = FillPath(route.path, route.codec.encode(props, route.defaults) as Params)
    return route.hash ? `#${path}` : path
  }

  // `fragment` is a plain anchor on the route's page ("#Channel", never a
  // "#/" route): the Router scrolls to it once the view is in.
  const Navigate = <K extends keyof R & string>(
    name?: K,
    data?: Partial<PropsOf<R[K]>>,
    fragment?: string
  ): void =>
    Write(
      `${name ? CreateHref(name, data) : location.pathname + location.search}${fragment ? `#${fragment}` : ""}`,
      false
    )

  // Same as Navigate but swaps the current history entry instead of pushing a
  // new one — for high-frequency incidental state (scroll position, zoom)
  // that would otherwise bury the back button.
  const Replace = <K extends keyof R & string>(name: K, data?: Partial<PropsOf<R[K]>>): void => {
    const href = CreateHref(name, data)
    if (href !== (href.startsWith("#") ? location.hash : location.pathname)) Write(href, true)
  }

  const GetState = (): { name: keyof R & string; data: Record<string, unknown> } | undefined => {
    const path = ReadPath()
    for (const route of routes) {
      const match = exec(path, route.pattern)
      if (match)
        return {
          name: route.name as keyof R & string,
          data: route.codec.decode(match.params, route.defaults),
        }
    }
    return undefined
  }

  // A real <a> (crawlable, middle-click, copy link) that navigates in place.
  const Link = <K extends keyof R & string>({
    name,
    data,
    fragment,
    ...props
  }: { name: K; data?: Partial<PropsOf<R[K]>>; fragment?: string } & Omit<
    JSX.HTMLAttributes<HTMLAnchorElement>,
    "href" | "data"
  >) => (
    <a
      {...props}
      href={`${CreateHref(name, data)}${fragment ? `#${fragment}` : ""}`}
      onClick={event => {
        if (event.button || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
        event.preventDefault()
        Navigate(name, data, fragment)
      }}
    />
  )

  // preact-iso's Router, fed from our own location instead of its
  // LocationProvider: no click hijacking, hash and path routes side by side.
  // `children` show while a view's chunk loads. `url` renders a given path
  // instead of the document's location — for a prerender, in node.
  const Router = <K extends keyof R & string>({
    children,
    name,
    data,
    url,
  }: {
    children?: ComponentChildren
    name?: K
    data?: Partial<PropsOf<R[K]>>
    url?: string
  }) => {
    const [path, setPath] = useState(() => url ?? ReadPath())
    const [loading, setLoading] = useState(false)

    useEffect(() => {
      const update = () => setPath(ReadPath())
      addEventListener("popstate", update)
      addEventListener("hashchange", update)
      return () => {
        removeEventListener("popstate", update)
        removeEventListener("hashchange", update)
      }
    }, [])

    // a plain fragment is an anchor on the page: reach it once the view (and a
    // lazy one's chunk) is in, as a page load would
    useEffect(() => {
      if (loading || !location.hash || location.hash.startsWith("#/")) return
      document.getElementById(decodeURIComponent(location.hash.slice(1)))?.scrollIntoView()
    }, [path, loading])

    const value = useMemo(
      () => ({
        url: path,
        path,
        // route state travels through the codec, never the query string
        query: {},
        route: (url: string, replace?: boolean) => Write(url, replace ?? false),
        back: () => history.back(),
        forward: () => history.forward(),
        wasPush: false,
      }),
      [path]
    )

    return (
      <LocationProvider.ctx.Provider value={value}>
        {loading && children}
        <IsoRouter onLoadEnd={() => setLoading(false)} onLoadStart={() => setLoading(true)}>
          {routes.map(route => (
            <Route component={route.Matched} key={route.name} path={route.pattern} />
          ))}
          {name ? <Route component={byName[name]!.Fallback} data={data} default /> : []}
        </IsoRouter>
      </LocationProvider.ctx.Provider>
    )
  }

  // The config as given: spread it into another router to host these routes
  // there — one router, so nothing nests.
  return { Router, Link, Navigate, Replace, GetState, CreateHref, routes: config as R }
}
